<template>
    <v-app>
        <v-container fluid fill-height v-if="checking">
            <v-layout align-center justify-center>
                <v-progress-circular :size="50" color="orange accent-3" indeterminate></v-progress-circular>
            </v-layout>
        </v-container>
        <v-container fluid fill-height v-else>
            <v-layout align-center justify-center>
                <v-flex xs11 sm6 md4 elevation-6>
                    <v-toolbar class="orange accent-3">
                        <v-toolbar-title class="white--text">
                            <v-icon dark large>mdi-bus</v-icon>
                            Delivery Kiosk
                        </v-toolbar-title>
                    </v-toolbar>
                    <v-card>
                        <v-card-text>
                            <v-text-field v-model="masked" label="รหัสผ่าน 6 หลัก" color="orange accent-3"
                                readonly height="50" single-line outlined
                                class="text-center" style="font-size: 32px"></v-text-field>

                            <v-row v-for="(row, r) in rows" :key="r" no-gutters justify="center">
                                <v-col v-for="key in row" :key="key" cols="4" class="pa-1">
                                    <v-btn block x-large :color="colorFor(key)" dark
                                        :disabled="busy" @click="press(key)">
                                        <span style="font-size: 22px">{{ labelFor(key) }}</span>
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-flex>
            </v-layout>

            <v-dialog v-model="busy" max-width="80" persistent>
                <div class="text-center">
                    <v-card><v-card-text>
                        <v-progress-circular :size="40" color="orange accent-5" indeterminate></v-progress-circular>
                    </v-card-text></v-card>
                </div>
            </v-dialog>

            <v-snackbar v-model="showError" color="red" :timeout="4000">{{ error }}</v-snackbar>
        </v-container>
    </v-app>
</template>
<script>
import driverService from '@/components/driverService'

// Resolves `promise`'s value, or `null` if it rejects or does not settle
// within `ms`. Used only around the cold-launch "am I already signed in"
// check: that check has no user action to retry it, so a hang there -- an
// unresolved rejection, or a network call that never settles at all -- must
// never be allowed to leave the driver looking at a permanent spinner. Both
// a rejection and a timeout resolve to `null` (treated as "not signed in",
// i.e. show the keypad), which is the safe direction: worst case the driver
// retypes a PIN they didn't need to.
function settleOrNull (promise, ms) {
    return new Promise(resolve => {
        let settled = false
        const timer = setTimeout(() => {
            if (settled) return
            settled = true
            resolve(null)
        }, ms)
        promise.then(
            value => {
                if (settled) return
                settled = true
                clearTimeout(timer)
                resolve(value)
            },
            () => {
                if (settled) return
                settled = true
                clearTimeout(timer)
                resolve(null)
            }
        )
    })
}

export default {
    data () {
        return {
            // True until the mount-time already-signed-in check settles. The
            // keypad must not flash before that redirect fires, so it stays
            // hidden behind the progress indicator until we know.
            checking: true,
            pin: '',
            busy: false,
            error: '',
            showError: false,
            rows: [['7', '8', '9'], ['4', '5', '6'], ['1', '2', '3'], ['C', '0', 'OK']]
        }
    },
    computed: {
        masked () {
            return this.pin.replace(/./g, '●')
        }
    },
    async created () {
        // The PWA's start_url is #/driver, so EVERY cold launch of the
        // installed app lands here — including a driver who signed in last
        // week and whose Firebase session is still valid. Firebase restores
        // that session ASYNCHRONOUSLY, so auth.currentUser reads null for a
        // beat even when the driver is in fact signed in; ensureDriverClaims()
        // awaits the same auth-ready gate the rest of the driver flow relies
        // on before it answers, so this is not a race against auth.currentUser.
        //
        // ensureDriverClaims() can also reach a genuine network round-trip
        // (refreshing an hourly-expired ID token) -- the normal case on a
        // next-morning cold boot -- and that call is unbounded. On a driver's
        // flaky signal that rejects or never settles, and this whole check has
        // no user action to retry it, so ANY failure here must fall through to
        // the keypad rather than leave `checking` stuck true. 15s gives the
        // module's own 10s auth-ready timeout room to finish normally before
        // the outer bound cuts it off.
        try {
            const driver = await settleOrNull(driverService.ensureDriverClaims(), 15000)
            if (driver) {
                // router.beforeEach gates the driver routes on store.state.user,
                // which App.vue's own onAuthStateChanged listener commits on a
                // separate async chain from the one just awaited above. Racing
                // ahead of that commit can bounce a genuinely signed-in driver
                // to the staff login screen.
                await this.waitForSignedInUser()
                this.$router.replace({ name: 'driverCar' }).catch(() => {})
            }
        } catch (e) {
            // Fall through to the keypad -- see settleOrNull above.
        } finally {
            this.checking = false
        }
    },
    beforeDestroy () {
        if (this._authWaitCleanup) {
            this._authWaitCleanup()
            this._authWaitCleanup = null
        }
    },
    methods: {
        colorFor (key) {
            if (key === 'C') return 'primary'
            if (key === 'OK') return 'green'
            return 'orange accent-4'
        },
        labelFor (key) {
            return key
        },
        press (key) {
            if (key === 'C') { this.pin = ''; return }
            if (key === 'OK') { this.submit(); return }
            if (this.pin.length < 6) this.pin += key
            // Six digits is the whole PIN, so there is nothing to wait for.
            if (this.pin.length === 6) this.submit()
        },
        fail (msg) {
            this.error = msg
            this.showError = true
            this.pin = ''
            this.busy = false
        },
        // Bridges the gap between Firebase Auth settling (which
        // driverService's ensureDriverClaims/signInWithPin await) and
        // store.state.user actually being committed -- that commit happens in
        // App.vue's own separate onAuthStateChanged listener. router.beforeEach
        // gates every driver route on store.state.user, so navigating before
        // that commit lands can send a driver who just typed the right PIN to
        // the staff login screen instead of driverCar. Bounded: on timeout we
        // navigate anyway -- a rare extra bounce beats being stuck here.
        //
        // Never commits to the store itself; App.vue's listener owns that.
        waitForSignedInUser (timeoutMs) {
            timeoutMs = timeoutMs || 2000
            return new Promise(resolve => {
                if (this.$store.state.user) { resolve(); return }
                const finish = () => {
                    clearTimeout(timer)
                    unwatch()
                    this._authWaitCleanup = null
                    resolve()
                }
                const timer = setTimeout(finish, timeoutMs)
                const unwatch = this.$store.watch(state => state.user, value => {
                    if (value) finish()
                })
                this._authWaitCleanup = finish
            })
        },
        async submit () {
            if (this.busy) return
            if (this.pin.length !== 6) {
                this.fail('โปรดระบุรหัสผ่านทั้งหมด 6 หลัก')
                return
            }
            this.busy = true
            try {
                await driverService.signInWithPin(this.pin)
                // Cleared here, not just in fail() -- a plaintext PIN should not
                // sit in reactive state through the waitForSignedInUser() gap
                // below, which is not necessarily instant.
                this.pin = ''
                await this.waitForSignedInUser()
                this.busy = false
                this.$router.push({ name: 'driverCar' }).catch(() => {})
            } catch (e) {
                // The callable throws permission-denied for a wrong PIN;
                // invalid-argument when the PIN isn't six digits;
                // resource-exhausted when the per-IP lockout has tripped
                // (10 failed attempts in 15 minutes) — that one must read as
                // "wait", not "wrong PIN", or a driver with the correct PIN
                // will keep retrying into the lockout. Anything else is a
                // network or deploy problem.
                if (e && e.code === 'permission-denied') this.fail('รหัสผ่านไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง')
                else if (e && e.code === 'invalid-argument') this.fail('รหัสผ่านต้องเป็นตัวเลข 6 หลัก')
                else if (e && e.code === 'resource-exhausted') this.fail('บัญชีถูกล็อกชั่วคราวจากการป้อนรหัสผ่านผิดหลายครั้ง กรุณารอสักครู่แล้วลองใหม่')
                else this.fail('เชื่อมต่อระบบไม่ได้ กรุณาลองใหม่อีกครั้ง')
            }
        }
    }
}
</script>
